import {
  greetingPhrasesToSendHistory,
  type WidgetSendHistoryItem,
} from '@teo/widget-shared';
import {
  getProactiveGreetingCache,
  waitForProactiveGreeting,
  type ProactiveGreetingCacheEntry,
} from './proactive-greeting-cache.js';
import {
  isGreetingIdCommitted,
  isGreetingOfferCommittable,
  getGreetingOfferState,
  resolveGreetingIdFromEntry,
} from './pending-greeting-offer.js';
import { getWidgetSession } from '../session/widget-session.js';

const FIRST_SEND_GREETING_WAIT_MS = 5_000;
const GREETING_PHRASE_INTERVAL_MS = 1_000;

export type GreetingCommitPayload = {
  history?: WidgetSendHistoryItem[];
  greetingId?: string;
};

/** Prefer offer shownAt (when user saw popup/welcome); else fetchedAt + delay. */
export function resolveGreetingBaseTimeMs(
  entry: {
    slug: string;
    pathname: string;
    fetchedAt?: number | null;
    delaySeconds?: number | null;
  },
): number {
  const offer = getGreetingOfferState(entry.slug, entry.pathname);
  if (offer?.shownAt != null && Number.isFinite(offer.shownAt)) {
    return offer.shownAt;
  }
  if (entry.fetchedAt != null) {
    return entry.fetchedAt + (entry.delaySeconds ?? 0) * 1_000;
  }
  return Date.now();
}

export function resolveHistoryFromGreetingEntry(
  entry: {
    shouldShow: boolean;
    greetingPhrases: ProactiveGreetingCacheEntry['greetingPhrases'];
    fetchedAt?: number | null;
    delaySeconds?: number | null;
    slug?: string;
    pathname?: string;
    baseTimeMs?: number;
  } | null | undefined,
  greetingId?: string,
): WidgetSendHistoryItem[] | undefined {
  if (!entry?.shouldShow || entry.greetingPhrases.length === 0) {
    return undefined;
  }

  const baseTimeMs =
    entry.baseTimeMs ??
    (entry.slug != null && entry.pathname != null
      ? resolveGreetingBaseTimeMs({
          slug: entry.slug,
          pathname: entry.pathname,
          fetchedAt: entry.fetchedAt ?? null,
          delaySeconds: entry.delaySeconds ?? null,
        })
      : entry.fetchedAt != null
        ? entry.fetchedAt + (entry.delaySeconds ?? 0) * 1_000
        : undefined);

  const history = greetingPhrasesToSendHistory(entry.greetingPhrases, {
    baseTimeMs,
    intervalMs: GREETING_PHRASE_INTERVAL_MS,
    greetingId,
  });

  return history.length > 0 ? history : undefined;
}

function resolveSessionHasThread(): boolean {
  const session = getWidgetSession();
  return session?.threadId != null && Number(session.threadId) > 0;
}

function resolveCommitFromEntry(
  entry: ProactiveGreetingCacheEntry,
): GreetingCommitPayload | undefined {
  const greetingId = resolveGreetingIdFromEntry(entry);
  const history = resolveHistoryFromGreetingEntry(entry, greetingId);
  if (!history?.length) return undefined;

  if (isGreetingIdCommitted(entry.slug, greetingId)) return undefined;
  if (!isGreetingOfferCommittable(entry.slug, entry.pathname, greetingId)) {
    return undefined;
  }

  return { history, greetingId };
}

/** Cached greeting as `history` for the first `POST /api/widget/send` when `thread_id` is null. */
export function resolveHistoryForFirstSend(
  slug: string,
  pathname: string,
): WidgetSendHistoryItem[] | undefined {
  if (resolveSessionHasThread()) return undefined;
  const entry = getProactiveGreetingCache(slug, pathname);
  const commit = entry ? resolveCommitFromEntry(entry) : undefined;
  return commit?.history;
}

export async function resolveHistoryForFirstSendAsync(
  options?: { waitMs?: number },
): Promise<WidgetSendHistoryItem[] | undefined> {
  const commit = await resolveGreetingCommitForSendAsync(options);
  return commit?.history;
}

/** Greeting commit for next send — `history` + optional `greeting_id` (first dialog or mid-thread). */
export async function resolveGreetingCommitForSendAsync(
  options?: { waitMs?: number },
): Promise<GreetingCommitPayload | undefined> {
  const session = getWidgetSession();
  if (!session?.slug || typeof window === 'undefined') {
    return undefined;
  }

  const pathname = window.location.pathname;

  let entry = getProactiveGreetingCache(session.slug, pathname);

  if (entry?.status === 'loading') {
    const waitMs = options?.waitMs ?? FIRST_SEND_GREETING_WAIT_MS;
    const result = await waitForProactiveGreeting(session.slug, pathname, {
      timeoutMs: waitMs,
    });
    entry = result.entry;
  }

  if (!entry || entry.status !== 'ready') return undefined;

  return resolveCommitFromEntry(entry);
}

export function resolveGreetingCommitForSend(
  slug: string,
  pathname: string,
): GreetingCommitPayload | undefined {
  const entry = getProactiveGreetingCache(slug, pathname);
  if (!entry || entry.status !== 'ready') return undefined;
  return resolveCommitFromEntry(entry);
}
